/**
 * Cloudflare Turnstile 通用工具模块
 *
 * 提供 Turnstile SDK 的加载、渲染、执行和重置功能。
 * 所有使用验证码发送的组件共享此模块，避免重复代码。
 */

// Turnstile Site Key（通过 Webpack DefinePlugin 或 env 注入）
const TURNSTILE_SITE_KEY =
  process.env.VUE_APP_TURNSTILE_SITE_KEY ||
  process.env.TURNSTILE_SITE_KEY ||
  '1x00000000000000000000AA'

// SDK 加载状态
let scriptLoaded = false
let scriptLoading = false
const loadCallbacks = []

/**
 * 动态加载 Cloudflare Turnstile SDK
 * @returns {Promise<void>}
 */
export function loadTurnstileScript() {
  return new Promise((resolve, reject) => {
    // 已加载
    if (scriptLoaded && window.turnstile) {
      resolve()
      return
    }

    // 正在加载中，排队等待
    if (scriptLoading) {
      loadCallbacks.push({ resolve, reject })
      return
    }

    scriptLoading = true

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true

    script.onload = () => {
      scriptLoaded = true
      scriptLoading = false
      resolve()
      // 通知所有等待中的回调
      loadCallbacks.forEach((cb) => cb.resolve())
      loadCallbacks.length = 0
    }

    script.onerror = (error) => {
      scriptLoading = false
      const err = new Error('Turnstile SDK 加载失败')
      reject(err)
      loadCallbacks.forEach((cb) => cb.reject(err))
      loadCallbacks.length = 0
      console.error('[turnstile] SDK 加载失败:', error)
    }

    document.head.appendChild(script)
    
    // 超时保护：如果网络被墙导致无响应，5秒后当作加载失败处理
    setTimeout(() => {
      if (!scriptLoaded && scriptLoading) {
        scriptLoading = false
        const err = new Error('Turnstile SDK 加载超时')
        reject(err)
        loadCallbacks.forEach((cb) => cb.reject(err))
        loadCallbacks.length = 0
        console.error('[turnstile] SDK 加载超时')
      }
    }, 5000)
  })
}

/**
 * 渲染 Turnstile widget（interaction-only 模式）
 * @param {string|HTMLElement} container - 容器元素或选择器
 * @param {object} options - 额外配置项
 * @returns {string} widgetId
 */
export function renderTurnstile(container, options = {}) {
  if (!window.turnstile) {
    console.error('[turnstile] SDK 未加载，请先调用 loadTurnstileScript()')
    return null
  }

  return window.turnstile.render(container, {
    sitekey: TURNSTILE_SITE_KEY,
    size: 'flexible',
    appearance: 'interaction-only',
    callback: options.onSuccess || (() => {}),
    'error-callback': options.onError || (() => {}),
    'expired-callback': options.onExpired || (() => {}),
    ...options,
  })
}

/**
 * 获取 Turnstile token（执行挑战）
 *
 * 对于 interaction-only 模式，调用 execute 后 Turnstile 会自动完成挑战并返回 token。
 * 如果 widget 已有有效的 response，直接返回。
 *
 * @param {string} widgetId - widget ID
 * @returns {Promise<string>} Turnstile token
 */
export function getTurnstileToken(widgetId) {
  return new Promise((resolve, reject) => {
    if (!window.turnstile) {
      reject(new Error('Turnstile SDK 未加载'))
      return
    }

    // 尝试获取已有的 response
    const existingResponse = window.turnstile.getResponse(widgetId)
    if (existingResponse) {
      resolve(existingResponse)
      return
    }

    // 重新渲染并等待回调（interaction-only 模式会自动执行挑战）
    // 设置超时保护
    const timeout = setTimeout(() => {
      reject(new Error('Turnstile 挑战超时'))
    }, 15000)

    // 使用 turnstile.execute 触发挑战
    try {
      window.turnstile.execute(widgetId, {
        callback: (token) => {
          clearTimeout(timeout)
          resolve(token)
        },
        'error-callback': () => {
          clearTimeout(timeout)
          reject(new Error('Turnstile 挑战失败'))
        },
      })
    } catch (e) {
      console.log('[turnstile] execute 不可用，回退到轮询模式:', e)
      clearTimeout(timeout)
      // 如果 execute 不可用，尝试轮询 getResponse
      const pollInterval = setInterval(() => {
        const response = window.turnstile.getResponse(widgetId)
        if (response) {
          clearInterval(pollInterval)
          resolve(response)
        }
      }, 500)

      // 10秒轮询超时
      setTimeout(() => {
        clearInterval(pollInterval)
        reject(new Error('Turnstile 获取 token 超时'))
      }, 10000)
    }
  })
}

/**
 * 重置 Turnstile widget（每次发送验证码后需重置）
 * @param {string} widgetId - widget ID
 */
export function resetTurnstile(widgetId) {
  if (window.turnstile) {
    window.turnstile.reset(widgetId)
  }
}

/**
 * 移除 Turnstile widget
 * @param {string} widgetId - widget ID
 */
export function removeTurnstile(widgetId) {
  if (window.turnstile && widgetId) {
    window.turnstile.remove(widgetId)
  }
}

/**
 * 获取 Turnstile Site Key
 * @returns {string}
 */
export function getTurnstileSiteKey() {
  return TURNSTILE_SITE_KEY
}
